import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { KeyValuePairList } from './key-value-pair-list';
import type { AppInfoFormData } from '@/types/integration-form-types';
import { AlertCircle, Loader2, Radar, X } from 'lucide-react';
import { useState } from 'react';

const CATEGORIES = [
    { value: '', label: 'Select category...' },
    { value: 'productivity', label: 'Productivity' },
    { value: 'communication', label: 'Communication' },
    { value: 'developer_tools', label: 'Developer Tools' },
    { value: 'crm', label: 'CRM' },
    { value: 'analytics', label: 'Analytics' },
    { value: 'storage', label: 'Storage' },
    { value: 'security', label: 'Security' },
    { value: 'finance', label: 'Finance' },
    { value: 'other', label: 'Other' },
];

interface WizardStepAppInfoProps {
    value: AppInfoFormData;
    onChange: (appInfo: AppInfoFormData) => void;
    errors?: Record<string, string>;
    nameRowSlot?: ReactNode;
    hideIdentityFields?: boolean;
    urlLabel?: string;
    onDiscover?: () => void;
    discovering?: boolean;
    discoverDisabled?: boolean;
    disabled?: boolean;
}

export function WizardStepAppInfo({
    value,
    onChange,
    errors = {},
    nameRowSlot,
    hideIdentityFields = false,
    urlLabel,
    onDiscover,
    discovering = false,
    discoverDisabled = false,
    disabled = false,
}: WizardStepAppInfoProps) {
    const [tagInput, setTagInput] = useState('');
    const update = (patch: Partial<AppInfoFormData>) => onChange({ ...value, ...patch });

    const addTag = () => {
        const tag = tagInput.trim().toLowerCase();
        if (tag && !value.tags.includes(tag)) {
            update({ tags: [...value.tags, tag] });
        }
        setTagInput('');
    };

    const removeTag = (tag: string) => {
        update({ tags: value.tags.filter((t) => t !== tag) });
    };

    const handleTagKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag();
        }
    };

    const resolvedUrlLabel = urlLabel ?? (value.protocol === 'mcp' ? 'MCP URL' : 'Base URL');

    const renderUrlField = (showHelp: boolean) => (
        <div className="space-y-1.5">
            <Label>{resolvedUrlLabel} <span className="text-red-500">*</span></Label>
            <div className="flex items-start gap-2">
                <Input
                    placeholder={value.protocol === 'mcp' ? 'https://mcp.example.com/mcp' : 'https://api.example.com/v1'}
                    value={value.baseUrl}
                    onChange={(e) => update({ baseUrl: e.target.value })}
                    className="font-mono text-sm flex-1"
                    disabled={disabled}
                />
                {onDiscover && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onDiscover}
                        disabled={discoverDisabled || discovering || !value.baseUrl.trim()}
                        className="shrink-0"
                    >
                        {discovering ? <Loader2 size={14} className="animate-spin" /> : <Radar size={14} />}
                        Discover
                    </Button>
                )}
            </div>
            {errors.baseUrl && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} /> {errors.baseUrl}
                </p>
            )}
            {showHelp && (
                <p className="text-xs text-slate-400">
                    {value.protocol === 'mcp'
                        ? 'MCP server endpoint. Discover probes supported features and auth metadata.'
                        : 'Root URL for all API endpoints. Method paths are appended to this.'}
                </p>
            )}
        </div>
    );

    return (
        <div className="space-y-5">
            {nameRowSlot ? (
                hideIdentityFields ? (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div className="flex flex-col gap-4 sm:col-span-2">
                            <div className="space-y-1.5">
                                <Label>Name</Label>
                                <Input
                                    placeholder="e.g. GitHub, Slack, Notion"
                                    value={value.name}
                                    onChange={(e) => update({ name: e.target.value })}
                                    disabled={disabled}
                                />
                                {errors.name && (
                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                        <AlertCircle size={12} /> {errors.name}
                                    </p>
                                )}
                            </div>
                            <div className="space-y-1.5 flex-1">
                                <Label>Description</Label>
                                <textarea
                                    placeholder="Brief description of the integration..."
                                    value={value.description}
                                    onChange={(e) => update({ description: e.target.value })}
                                    rows={2}
                                    disabled={disabled}
                                    className="w-full h-[calc(100%-1.75rem)] min-h-16 rounded-md border border-slate-200 bg-white py-2 px-3 text-sm text-slate-700 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none disabled:opacity-50"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Tool Capability</Label>
                            {nameRowSlot}
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        {/* Left: Name + Description stacked (2/3 width) */}
                        <div className="flex flex-col gap-4 sm:col-span-2">
                            <div className="space-y-1.5">
                                <Label>App Name <span className="text-red-500">*</span></Label>
                                <Input
                                    placeholder="e.g. GitHub, Slack, Notion"
                                    value={value.name}
                                    onChange={(e) => update({ name: e.target.value })}
                                    autoFocus
                                />
                                {errors.name && (
                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                        <AlertCircle size={12} /> {errors.name}
                                    </p>
                                )}
                            </div>
                            <div className="space-y-1.5 flex-1">
                                <Label>Description</Label>
                                <textarea
                                    placeholder="Brief description of the integration..."
                                    value={value.description}
                                    onChange={(e) => update({ description: e.target.value })}
                                    rows={2}
                                    className="w-full h-[calc(100%-1.75rem)] min-h-16 rounded-md border border-slate-200 bg-white py-2 px-3 text-sm text-slate-700 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                                />
                            </div>
                        </div>
                        {/* Right: Tool Capability slot */}
                        <div className="space-y-1.5">
                            <Label>Tool Capability</Label>
                            {nameRowSlot}
                        </div>
                    </div>
                )
            ) : !hideIdentityFields ? (
                <>
                    {/* Row 1: Name (full width) */}
                    <div className="space-y-1.5">
                        <Label>App Name <span className="text-red-500">*</span></Label>
                        <Input
                            placeholder="e.g. GitHub, Slack, Notion"
                            value={value.name}
                            onChange={(e) => update({ name: e.target.value })}
                            autoFocus
                        />
                        {errors.name && (
                            <p className="text-xs text-red-500 flex items-center gap-1">
                                <AlertCircle size={12} /> {errors.name}
                            </p>
                        )}
                    </div>
                    {/* Row 2: Description (full width) */}
                    <div className="space-y-1.5">
                        <Label>Description</Label>
                        <textarea
                            placeholder="Brief description of the integration..."
                            value={value.description}
                            onChange={(e) => update({ description: e.target.value })}
                            rows={2}
                            className="w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm text-slate-700 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                        />
                    </div>
                </>
            ) : null}

            {/* Row 3: Category + Tags + Vendor */}
            {hideIdentityFields ? (
                renderUrlField(false)
            ) : (
                <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                        <Label>Category</Label>
                        <Select value={value.category} onValueChange={(v) => update({ category: v })}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select category..." />
                            </SelectTrigger>
                            <SelectContent>
                                {CATEGORIES.filter((c) => c.value).map((cat) => (
                                    <SelectItem key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Tags</Label>
                        {value.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-1">
                                {value.tags.map((tag) => (
                                    <Badge key={tag} variant="secondary" className="text-xs gap-1">
                                        {tag}
                                        <button type="button" onClick={() => removeTag(tag)} className="ml-0.5 hover:text-red-500">
                                            <X size={10} />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                        <Input
                            placeholder="Type a tag and press Enter"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={handleTagKeyDown}
                            onBlur={addTag}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Vendor</Label>
                        <Input
                            placeholder="e.g. Microsoft, Google"
                            value={value.vendor}
                            onChange={(e) => update({ vendor: e.target.value })}
                        />
                    </div>
                </div>
            )}

            {/* Row 4: Vendor + Logo URL + Documentation URL (overview) or Logo + Docs (wizard, URL already shown) */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {hideIdentityFields && (
                    <div className="space-y-1.5">
                        <Label>Vendor</Label>
                        <Input
                            placeholder="e.g. Microsoft, Google"
                            value={value.vendor}
                            onChange={(e) => update({ vendor: e.target.value })}
                        />
                    </div>
                )}
                {!hideIdentityFields && renderUrlField(true)}
                <div className="space-y-1.5">
                    <Label>Logo URL</Label>
                    <div className="flex items-center gap-3">
                        <Input
                            placeholder="https://cdn.example.com/icon.svg"
                            value={value.iconUrl}
                            onChange={(e) => update({ iconUrl: e.target.value })}
                            className="flex-1"
                        />
                        {value.iconUrl && (
                            <img
                                src={value.iconUrl}
                                alt="Preview"
                                className="h-9 w-9 rounded-md border border-slate-200 object-contain bg-white"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                        )}
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label>Documentation URL</Label>
                    <Input
                        placeholder="https://docs.example.com/api"
                        value={value.documentationUrl}
                        onChange={(e) => update({ documentationUrl: e.target.value })}
                    />
                </div>
            </div>

            {/* Row 6: Common Headers (full width) */}
            <div className="space-y-1.5">
                <Label>Common Headers</Label>
                <p className="text-xs text-slate-400">
                    Headers sent with every request to this API.
                </p>
                <KeyValuePairList
                    value={value.commonHeaders}
                    onChange={(commonHeaders) => update({ commonHeaders })}
                    keyPlaceholder="Header name"
                    valuePlaceholder="Header value"
                    addLabel="Add Header"
                />
            </div>
        </div>
    );
}
